import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api } from '@/lib/api-client';
import { z } from 'zod';
import {
  auditoriaCadastroSchema,
  bensDireitosSchema,
  clienteDetailSchema,
  clienteUpdateSchema,
  clientesListSchema,
  documentoSchema,
  enriquecerAplicarSchema,
  enriquecerPreviewSchema,
  enriquecerProgressoSchema,
  enriquecerStartSchema,
  imovelCreateSchema,
  imovelFullSchema,
  imovelUpdateSchema,
  okSchema,
  processosAmbientalSchema,
  uploadDocResponseSchema,
  type ClienteUpdateInput,
  type ImovelCreateInput,
  type ImovelUpdateInput,
} from './schemas';

const keys = {
  list: () => ['clientes', 'list'] as const,
  detail: (cpf: string) => ['clientes', 'detail', cpf] as const,
  ambiental: (cpf: string) => ['clientes', 'ambiental', cpf] as const,
  bens: (cpf: string) => ['clientes', 'bens', cpf] as const,
  auditoria: (cpf: string) => ['clientes', 'auditoria', cpf] as const,
  enriquecer: (cpf: string) => ['clientes', 'enriquecer', cpf] as const,
  documentos: (cpf: string) => ['clientes', 'documentos', cpf] as const,
  documentosImovel: (imovelId: string) => ['imoveis', imovelId, 'documentos'] as const,
};

export function useClientes() {
  return useQuery({
    queryKey: keys.list(),
    queryFn: ({ signal }) =>
      api('/api/v2/clientes', { schema: clientesListSchema, signal }),
    staleTime: 30_000,
  });
}

export function useCliente(cpf: string | null) {
  return useQuery({
    queryKey: keys.detail(cpf ?? ''),
    queryFn: ({ signal }) =>
      api(`/api/v2/clientes/${encodeURIComponent(cpf ?? '')}`, {
        schema: clienteDetailSchema,
        signal,
      }),
    enabled: !!cpf,
    staleTime: 30_000,
  });
}

export function useAuditoriaCadastro(cpf: string | null) {
  return useQuery({
    queryKey: keys.auditoria(cpf ?? ''),
    queryFn: ({ signal }) =>
      api(`/api/v2/clientes/${encodeURIComponent(cpf ?? '')}/auditoria-cadastro`, {
        schema: auditoriaCadastroSchema,
        signal,
      }),
    enabled: !!cpf,
    staleTime: 30_000,
    retry: false,
  });
}

export function useClienteAmbiental(cpf: string | null) {
  return useQuery({
    queryKey: keys.ambiental(cpf ?? ''),
    queryFn: ({ signal }) =>
      api(`/api/v2/processos-ambiental?cpf=${encodeURIComponent(cpf ?? '')}`, {
        schema: processosAmbientalSchema,
        signal,
      }),
    enabled: !!cpf,
    staleTime: 60_000,
    retry: false,
  });
}

export function useUpdateCliente(cpf: string | null) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: ClienteUpdateInput) =>
      api(`/api/v2/clientes/${encodeURIComponent(cpf ?? '')}`, {
        method: 'PATCH',
        body: clienteUpdateSchema.parse(input),
        schema: okSchema,
      }),
    onSuccess: () => {
      if (cpf) {
        void qc.invalidateQueries({ queryKey: keys.detail(cpf) });
      }
      void qc.invalidateQueries({ queryKey: keys.list() });
    },
  });
}

export function useDeleteCliente() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (cpf: string) =>
      api(`/api/v2/clientes/${encodeURIComponent(cpf)}`, {
        method: 'DELETE',
        schema: okSchema,
      }),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: keys.list() });
    },
  });
}

// ─── Bens & Direitos (IRPF) ────────────────────────────────────────────
export function useBensDireitos(cpf: string | null) {
  return useQuery({
    queryKey: keys.bens(cpf ?? ''),
    queryFn: ({ signal }) =>
      api(`/api/v2/clientes/${encodeURIComponent(cpf ?? '')}/bens-direitos`, {
        schema: bensDireitosSchema,
        signal,
      }),
    enabled: !!cpf,
    staleTime: 60_000,
    retry: false,
  });
}

// ─── Imóvel CRUD ──────────────────────────────────────────────────────
export function useCreateImovel(cpfDono: string | null) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: ImovelCreateInput) =>
      api('/api/v2/imoveis', {
        method: 'POST',
        body: imovelCreateSchema.parse(input),
        schema: imovelFullSchema,
      }),
    onSuccess: () => {
      if (cpfDono) {
        void qc.invalidateQueries({ queryKey: keys.detail(cpfDono) });
      }
      void qc.invalidateQueries({ queryKey: keys.list() });
    },
  });
}

export function useUpdateImovel(cpfDono: string | null) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, patch }: { id: string; patch: ImovelUpdateInput }) =>
      api(`/api/v2/imoveis/${encodeURIComponent(id)}`, {
        method: 'PATCH',
        body: imovelUpdateSchema.parse(patch),
        schema: okSchema,
      }),
    onSuccess: () => {
      if (cpfDono) {
        void qc.invalidateQueries({ queryKey: keys.detail(cpfDono) });
      }
    },
  });
}

export function useDeleteImovel(cpfDono: string | null) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) =>
      api(`/api/v2/imoveis/${encodeURIComponent(id)}`, {
        method: 'DELETE',
        schema: okSchema,
      }),
    onSuccess: () => {
      if (cpfDono) {
        void qc.invalidateQueries({ queryKey: keys.detail(cpfDono) });
      }
      void qc.invalidateQueries({ queryKey: keys.list() });
    },
  });
}

// ─── Documentos (list, upload, delete) ───────────────────────────────────────
const documentosListSchema = z.array(documentoSchema);

export function useDocumentos(cpf: string | null) {
  return useQuery({
    queryKey: keys.documentos(cpf ?? ''),
    queryFn: ({ signal }) =>
      api(`/api/v2/documentos?cliente_cpf=${encodeURIComponent(cpf ?? '')}`, {
        schema: documentosListSchema,
        signal,
      }),
    enabled: !!cpf,
    staleTime: 30_000,
  });
}

// O detalhe de imóvel também é aberto pela tela de grupo econômico, onde não há
// um CPF individual em contexto. Nesse caso, consulta os documentos diretamente
// pelo imóvel para manter disponíveis relatórios e anexos vinculados a ele.
export function useDocumentosImovel(imovelId: string | null) {
  return useQuery({
    queryKey: keys.documentosImovel(imovelId ?? ''),
    queryFn: ({ signal }) =>
      api(`/api/v2/documentos?imovel_id=${encodeURIComponent(imovelId ?? '')}`, {
        schema: documentosListSchema,
        signal,
      }),
    enabled: !!imovelId,
    staleTime: 30_000,
  });
}

// Upload completo via /api/v2/documentos/upload — multi-arquivo + tipo + descrição + validade + imóvel.
export function useUploadDocumento(cpf: string | null) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({
      file,
      tipo,
      descricao,
      validade,
      imovelId,
    }: {
      file: File;
      tipo: string;
      descricao?: string;
      validade?: string;
      imovelId?: string;
    }) => {
      const fd = new FormData();
      fd.append('file', file);
      fd.append('cliente_cpf', cpf ?? '');
      fd.append('tipo', tipo);
      if (descricao) fd.append('descricao', descricao);
      if (validade) fd.append('validade', validade);
      if (imovelId) fd.append('imovel_id', imovelId);
      return api('/api/v2/documentos/upload', {
        method: 'POST',
        body: fd,
        schema: uploadDocResponseSchema,
      });
    },
    onSuccess: () => {
      if (cpf) {
        void qc.invalidateQueries({ queryKey: keys.documentos(cpf) });
        void qc.invalidateQueries({ queryKey: keys.detail(cpf) });
      }
    },
  });
}

export function useDeleteDocumento(cpf: string | null) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (docId: string | number) =>
      api(`/api/v2/documentos/${docId}`, {
        method: 'DELETE',
        schema: okSchema,
      }),
    onSuccess: () => {
      if (cpf) {
        void qc.invalidateQueries({ queryKey: keys.documentos(cpf) });
        void qc.invalidateQueries({ queryKey: keys.detail(cpf) });
      }
    },
  });
}

// ─── Solo (pedologia RADAM/IBGE cruzada com polígono CAR) ─────────────
const soloClasseSchema = z.object({
  ordem: z.string().nullable().optional(),
  subordem: z.string().nullable().optional(),
  grande_grupo: z.string().nullable().optional(),
  subgrupos: z.string().nullable().optional(),
  textura: z.string().nullable().optional(),
  relevo: z.string().nullable().optional(),
  componente: z.string().nullable().optional(),
  ha: z.number(),
  pct: z.number(),
  geojson: z.any().nullable(),
});

const soloResponseSchema = z.object({
  imovel: z.object({
    id: z.number(),
    denominacao: z.string().nullable(),
    area_total_ha: z.union([z.string(), z.number()]).nullable().optional(),
    car_codigo: z.string().nullable(),
  }),
  car: z.object({
    cod_imovel: z.string(),
    area_ha: z.number(),
    municipio: z.string().nullable(),
    uf: z.string().nullable(),
    bbox: z.tuple([z.number(), z.number(), z.number(), z.number()]),
    centroide: z.any(),
    geojson: z.any().nullable(),
  }),
  solo: z.object({
    fonte: z.string(),
    escala: z.string(),
    ano_referencia: z.number(),
    total_ha: z.number(),
    classes: z.array(soloClasseSchema),
  }),
});

export type SoloResponse = z.infer<typeof soloResponseSchema>;
export type SoloClasse = z.infer<typeof soloClasseSchema>;

// SoilGrids (ISRIC) — propriedades químicas/físicas em 6 profundidades
const soilgridsDepthValueSchema = z.object({
  depth: z.string(),
  value: z.number().nullable(),
});

const soilgridsPropertySchema = z.object({
  label: z.string(),
  unit: z.string(),
  depths: z.array(soilgridsDepthValueSchema),
});

const soilgridsResponseSchema = z.object({
  imovel: z.object({
    id: z.number(),
    denominacao: z.string().nullable(),
    car_codigo: z.string().nullable(),
  }),
  car: z.object({
    cod_imovel: z.string(),
    area_ha: z.number(),
    municipio: z.string().nullable(),
    uf: z.string().nullable(),
    bbox: z.tuple([z.number(), z.number(), z.number(), z.number()]),
  }),
  soilgrids: z.object({
    fonte: z.string(),
    escala: z.string(),
    pontos_amostrados: z.number(),
    pontos_ok: z.number(),
    depths: z.array(z.string()),
    properties: z.record(soilgridsPropertySchema),
  }),
  cache: z
    .object({
      hit: z.boolean(),
      fetched_at: z.union([z.string(), z.date()]).optional(),
    })
    .optional(),
});

export type SoilGridsResponse = z.infer<typeof soilgridsResponseSchema>;
export type SoilGridsProperty = z.infer<typeof soilgridsPropertySchema>;

// Recomendação agronômica — cruzamento SoilGrids + RADAM + WoSIS
const recomendacaoSchema = z.object({
  imovel: z.object({
    id: z.number(),
    denominacao: z.string().nullable(),
    car_codigo: z.string().nullable(),
  }),
  perfil: z.object({
    ph: z.number().nullable(),
    ph_subsolo: z.number().nullable(),
    ctc: z.number().nullable(),
    soc: z.number().nullable(),
    n: z.number().nullable(),
    argila: z.number().nullable(),
    areia: z.number().nullable(),
    silte: z.number().nullable(),
    bdod: z.number().nullable(),
    classe_radam: z.object({
      ordem: z.string().nullable(),
      subordem: z.string().nullable(),
      grande_grupo: z.string().nullable(),
      textura: z.string().nullable(),
      relevo: z.string().nullable(),
    }).nullable(),
    perfil_wosis_proximo: z.object({
      codigo: z.string().nullable(),
      ano: z.number().nullable(),
      distancia_km: z.number(),
      horizontes: z.number(),
    }).nullable(),
  }),
  limitacoes: z.array(z.object({
    tipo: z.string(),
    sev: z.enum(['alta', 'media', 'baixa']),
    desc: z.string(),
  })),
  intervencoes: z.array(z.object({
    acao: z.string(),
    dose: z.string().optional(),
    justificativa: z.string(),
  })),
  culturas: z.array(z.object({
    nome: z.string(),
    score: z.number().nullable(),
    score_pct: z.number().nullable(),
    aptidao: z.enum(['apta', 'regular', 'marginal', 'inapta', 'sem_dados']),
    limitantes: z.array(z.string()),
  })),
  fontes: z.object({
    soilgrids: z.any().nullable(),
    radam: z.string().nullable(),
    wosis: z.string().nullable(),
  }),
  avisos: z.array(z.string()),
});

export type RecomendacaoResponse = z.infer<typeof recomendacaoSchema>;

export function useImovelRecomendacao(imovelId: string | number | null) {
  return useQuery({
    queryKey: ['imovel', String(imovelId ?? ''), 'recomendacao'] as const,
    queryFn: ({ signal }) =>
      api(`/api/v2/geo/solo/${encodeURIComponent(String(imovelId))}/recomendacao`, {
        schema: recomendacaoSchema,
        signal,
      }),
    enabled: !!imovelId,
    staleTime: 10 * 60_000,
    retry: false,
  });
}

// WoSIS — perfis amostrados via GraphQL
const wosisLayerSchema = z.object({
  name: z.string().nullable(),
  upperDepth: z.number().nullable(),
  lowerDepth: z.number().nullable(),
  organicSurface: z.boolean().nullable(),
  clay: z.number().nullable(),
  sand: z.number().nullable(),
  silt: z.number().nullable(),
  phaq: z.number().nullable(),
  phkc: z.number().nullable(),
  orgc: z.number().nullable(),
  nitkjd: z.number().nullable(),
  cecph7: z.number().nullable(),
  bdfi33l: z.number().nullable(),
  cfvo: z.number().nullable(),
});

const wosisProfileSchema = z.object({
  profileId: z.number(),
  profileCode: z.string().nullable(),
  datasetCode: z.string().nullable(),
  year: z.number().nullable(),
  latitude: z.number(),
  longitude: z.number(),
  countryName: z.string().nullable(),
  region: z.string().nullable(),
  distance_km: z.number(),
  wrbReferenceSoilGroup: z.string().nullable(),
  wrbPrincipalQualifiers: z.string().nullable(),
  wrbPublicationYear: z.number().nullable(),
  faoMajorGroup: z.string().nullable(),
  faoSoilUnit: z.string().nullable(),
  faoPublicationYear: z.number().nullable(),
  usdaOrderName: z.string().nullable(),
  usdaSuborder: z.string().nullable(),
  usdaGreatGroup: z.string().nullable(),
  usdaSubgroup: z.string().nullable(),
  layers: z.array(wosisLayerSchema),
});

const wosisResponseSchema = z.object({
  imovel: z.object({
    id: z.number(),
    denominacao: z.string().nullable(),
    car_codigo: z.string().nullable(),
  }),
  car: z.object({
    cod_imovel: z.string(),
    municipio: z.string().nullable(),
    uf: z.string().nullable(),
    centroide: z.object({ lat: z.number(), lon: z.number() }),
    bbox: z.tuple([z.number(), z.number(), z.number(), z.number()]),
  }),
  wosis: z.object({
    fonte: z.string(),
    radius_km: z.number(),
    total: z.number(),
    profiles: z.array(wosisProfileSchema),
  }),
  cache: z
    .object({
      hit: z.boolean(),
      fetched_at: z.union([z.string(), z.date()]).optional(),
    })
    .optional(),
});

export type WosisResponse = z.infer<typeof wosisResponseSchema>;
export type WosisProfile = z.infer<typeof wosisProfileSchema>;
export type WosisLayer = z.infer<typeof wosisLayerSchema>;

export function useImovelWosisProfiles(
  imovelId: string | number | null,
  opts?: { radiusKm?: number; limit?: number },
) {
  const radiusKm = opts?.radiusKm ?? 100;
  const limit = opts?.limit ?? 12;
  return useQuery({
    queryKey: ['imovel', String(imovelId ?? ''), 'wosis', radiusKm, limit] as const,
    queryFn: ({ signal }) =>
      api(
        `/api/v2/geo/solo/${encodeURIComponent(String(imovelId))}/wosis-profiles?radius_km=${radiusKm}&limit=${limit}`,
        { schema: wosisResponseSchema, signal },
      ),
    enabled: !!imovelId,
    staleTime: 30 * 60_000,
    retry: false,
  });
}

export function useImovelSoilGrids(imovelId: string | number | null, opts?: { refresh?: boolean }) {
  return useQuery({
    queryKey: ['imovel', String(imovelId ?? ''), 'soilgrids', opts?.refresh ? 1 : 0] as const,
    queryFn: ({ signal }) =>
      api(
        `/api/v2/geo/solo/${encodeURIComponent(String(imovelId))}/soilgrids${opts?.refresh ? '?refresh=1' : ''}`,
        { schema: soilgridsResponseSchema, signal },
      ),
    enabled: !!imovelId,
    staleTime: 30 * 60_000,
    retry: false,
  });
}

export function useImovelSolo(imovelId: string | number | null, fonte: '250k' | '5m' = '250k') {
  return useQuery({
    queryKey: ['imovel', String(imovelId ?? ''), 'solo', fonte] as const,
    queryFn: ({ signal }) =>
      api(`/api/v2/geo/solo/${encodeURIComponent(String(imovelId))}?fonte=${fonte}`, {
        schema: soloResponseSchema,
        signal,
      }),
    enabled: !!imovelId,
    staleTime: 5 * 60_000,
    retry: false,
  });
}

// Mantido por compat com upload por seção (RG/conjuge/etc) via endpoint legado.
export function useUploadDocCliente(cpf: string | null) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ file, secao }: { file: File; secao: string }) => {
      const fd = new FormData();
      fd.append('file', file);
      fd.append('secao', secao);
      return api(`/api/v2/clientes/${encodeURIComponent(cpf ?? '')}/upload-doc`, {
        method: 'POST',
        body: fd,
        schema: uploadDocResponseSchema,
      });
    },
    onSuccess: () => {
      if (cpf) void qc.invalidateQueries({ queryKey: keys.detail(cpf) });
    },
  });
}

// ─── Enriquecer (varrer integrações → preview → aplicar) ─────────────
export function useEnriquecerStart(cpf: string | null) {
  return useMutation({
    mutationFn: () =>
      api(`/api/v2/clientes/${encodeURIComponent(cpf ?? '')}/enriquecer/varrer`, {
        method: 'POST',
        schema: enriquecerStartSchema,
      }),
  });
}

// Polling — ligado/desligado pelo flag `active`.
export function useEnriquecerProgresso(cpf: string | null, active: boolean) {
  return useQuery({
    queryKey: [...keys.enriquecer(cpf ?? ''), 'progresso'] as const,
    queryFn: ({ signal }) =>
      api(`/api/v2/clientes/${encodeURIComponent(cpf ?? '')}/enriquecer/progresso`, {
        schema: enriquecerProgressoSchema,
        signal,
      }),
    enabled: !!cpf && active,
    refetchInterval: active ? 2_000 : false,
    staleTime: 0,
    retry: false,
  });
}

export function useEnriquecerPreview(cpf: string | null, enabled: boolean) {
  return useQuery({
    queryKey: [...keys.enriquecer(cpf ?? ''), 'preview'] as const,
    queryFn: ({ signal }) =>
      api(`/api/v2/clientes/${encodeURIComponent(cpf ?? '')}/enriquecer/preview`, {
        schema: enriquecerPreviewSchema,
        signal,
      }),
    enabled: !!cpf && enabled,
    staleTime: 30_000,
    retry: false,
  });
}

export function useEnriquecerAplicar(cpf: string | null) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: () =>
      api(`/api/v2/clientes/${encodeURIComponent(cpf ?? '')}/enriquecer/aplicar`, {
        method: 'POST',
        schema: enriquecerAplicarSchema,
      }),
    onSuccess: () => {
      if (cpf) {
        void qc.invalidateQueries({ queryKey: keys.detail(cpf) });
        void qc.invalidateQueries({ queryKey: keys.list() });
      }
    },
  });
}
